/**
 * Renders truncated text inline; on click shows full text in a Radix popover.
 * Threshold: > 30 chars triggers popover.
 * At most one popover is open at a time across all instances.
 */
import { useState, useEffect, useRef } from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

const CLOSE_EVENT = "pe-close-popovers";

interface TextPopoverProps {
  value: string;
  maxDisplay?: number;
}

export default function TextPopover({
  value,
  maxDisplay = 30,
}: TextPopoverProps) {
  const [open, setOpen] = useState(false);
  const isSelf = useRef(false);
  const isLong = value.length > maxDisplay;

  // Close when another TextPopover opens
  useEffect(() => {
    function onClose() {
      if (!isSelf.current) setOpen(false);
    }
    document.addEventListener(CLOSE_EVENT, onClose);
    return () => document.removeEventListener(CLOSE_EVENT, onClose);
  }, []);

  function handleOpenChange(next: boolean) {
    if (next) {
      isSelf.current = true;
      document.dispatchEvent(new CustomEvent(CLOSE_EVENT));
      isSelf.current = false;
    }
    setOpen(next);
  }

  if (!isLong) return <span>{value}</span>;

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <PopoverPrimitive.Trigger asChild>
        <span className="cursor-pointer hover:text-blue-700 transition-colors">
          {value.slice(0, maxDisplay)}
          <span className="font-bold">…</span>
        </span>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side="top"
          align="start"
          sideOffset={6}
          className="z-[9999] max-w-sm rounded-lg border border-blue-200 bg-white p-4 text-sm text-gray-800 shadow-xl leading-relaxed
            data-[state=open]:animate-in data-[state=closed]:animate-out
            data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0
            data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
        >
          <p className="text-xs font-bold uppercase tracking-wide text-blue-700 mb-2 border-b border-gray-100 pb-2">
            Full value
          </p>
          <p className="whitespace-pre-wrap">{value}</p>
          <PopoverPrimitive.Arrow className="fill-white drop-shadow-sm" />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
